import { Link } from "react-router-dom";
import { MapPin, Eye, BadgeCheck, Building2, User, Sprout } from "lucide-react";
import { StatusBadge } from "@/components/StatusBadge";
import { FavoriteButton } from "@/components/FavoriteButton";
import { priceLabel, timeAgo } from "@/lib/format";
import { imageUrl } from "@/lib/api";

export function ProductCard({ listing, favored, onFav }) {
  const seller = listing.seller || {};
  const img = imageUrl((listing.images || [])[0]);
  return (
    <Link
      to={`/ilan/${listing.listing_id}`}
      data-testid={`product-card-${listing.listing_id}`}
      className="group flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm transition-all duration-200 hover:shadow-md hover:-translate-y-0.5"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
        {img ? (
          <img
            src={img}
            alt={listing.title}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full items-center justify-center text-slate-300">Görsel yok</div>
        )}
        <div className="absolute left-2.5 top-2.5">
          <StatusBadge condition={listing.condition} />
        </div>
        <div className="absolute right-2.5 top-2.5">
          <FavoriteButton listingId={listing.listing_id} initial={favored} onToggle={onFav} />
        </div>
        {listing.is_featured && (
          <span className="absolute bottom-2.5 left-2.5 rounded-md bg-emerald-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
            Öne Çıkan
          </span>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-1.5 p-3.5">
        <p className="text-lg font-bold leading-tight text-emerald-900">{priceLabel(listing)}</p>
        <h3 className="line-clamp-2 min-h-[2.5rem] text-sm font-medium text-slate-800">{listing.title}</h3>

        {listing.is_agriculture && (
          <span className="inline-flex w-fit items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700" data-testid="producer-badge">
            <Sprout size={11} /> Doğrudan Üreticiden
          </span>
        )}

        {listing.min_order != null && (
          <p className="text-xs text-slate-500">
            Min. sipariş: <span className="font-semibold text-slate-700">{listing.min_order} {listing.unit}</span>
          </p>
        )}

        <div className="mt-1 flex items-center gap-1 text-xs text-slate-500">
          <MapPin size={13} className="shrink-0" />
          <span className="truncate">
            {listing.city}{listing.district ? ` / ${listing.district}` : ""}
          </span>
        </div>

        <div className="mt-auto flex items-center justify-between border-t border-slate-100 pt-2">
          <div className="flex items-center gap-1 text-xs text-slate-600">
            {seller.account_type === "kurumsal" ? (
              <Building2 size={13} className="shrink-0 text-slate-400" />
            ) : (
              <User size={13} className="shrink-0 text-slate-400" />
            )}
            <span className="truncate max-w-[120px]">{seller.name}</span>
            {seller.corporate_approved && <BadgeCheck size={14} className="shrink-0 text-emerald-500" />}
          </div>
          <span className="flex items-center gap-1 text-[11px] text-slate-400">
            <Eye size={12} /> {listing.views || 0}
          </span>
        </div>
      </div>
    </Link>
  );
}

export function ProductCardSkeleton() {
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <div className="aspect-[4/3] animate-pulse bg-slate-100" />
      <div className="space-y-2 p-3.5">
        <div className="h-5 w-24 animate-pulse rounded bg-slate-100" />
        <div className="h-4 w-full animate-pulse rounded bg-slate-100" />
        <div className="h-3 w-20 animate-pulse rounded bg-slate-100" />
      </div>
    </div>
  );
}
